import { Controller } from '@hotwired/stimulus'

/**
 * Device Preview Controller
 *
 * Controls the preview content width to simulate different device sizes.
 * Used for responsive design preview in the page editor.
 *
 * Features:
 * - Device width simulation (mobile, tablet, desktop)
 * - Breakpoint indicator showing current responsive breakpoint
 * - Smooth transitions between sizes
 * - Integration with Style Inspector for synced breakpoint changes
 *
 * Usage:
 *   <div data-controller="device-preview" data-device-preview-show-indicator-value="true">
 *     <button data-action="device-preview#setWidth" data-device-preview-width-param="375">Mobile</button>
 *     <button data-action="device-preview#setWidth" data-device-preview-width-param="768">Tablet</button>
 *     <button data-action="device-preview#setWidth" data-device-preview-width-param="100%">Full</button>
 *   </div>
 *
 * Breakpoint Targets (optional):
 *   <span data-device-preview-target="indicator">base</span>
 *   <span data-device-preview-target="dimensions">375 x 667</span>
 */
export default class extends Controller {
  static targets = ['indicator', 'dimensions']

  static values = {
    currentWidth: { type: String, default: '100%' },
    showIndicator: { type: Boolean, default: false }
  }

  // Breakpoint definitions matching ResponsiveRulesService
  static breakpoints = {
    base: { min: 0, max: 639, label: 'Mobile', icon: '📱' },
    sm: { min: 640, max: 767, label: 'Small', icon: '📱' },
    md: { min: 768, max: 1023, label: 'Tablet', icon: '📲' },
    lg: { min: 1024, max: 1279, label: 'Desktop', icon: '💻' },
    xl: { min: 1280, max: 1535, label: 'Wide', icon: '🖥️' },
    '2xl': { min: 1536, max: Infinity, label: 'Ultra Wide', icon: '🖥️' }
  }

  connect() {
    // Look for dedicated preview wrapper first, then main content element
    // Avoid falling back to document.body to prevent affecting toolbar/fixed UI
    this.contentWrapper = document.querySelector('.preview-content-wrapper') ||
                          document.querySelector('main.flex-grow') ||
                          document.querySelector('main') ||
                          document.querySelector('.page-content')

    if (!this.contentWrapper) {
      console.warn('[DevicePreview] No content wrapper found - device preview may affect fixed UI')
      this.contentWrapper = document.body
    }

    this.updateActiveButton('100%')
    this.updateBreakpointIndicator('100%')

    // Listen for breakpoint changes from Style Inspector
    this.handleBreakpointChange = this.handleBreakpointChange.bind(this)
    window.addEventListener('breakpoint-changed', this.handleBreakpointChange)

    // Listen for window resize to update indicator when in full-width mode
    this.handleResize = this.handleResize.bind(this)
    window.addEventListener('resize', this.handleResize)
  }

  disconnect() {
    window.removeEventListener('breakpoint-changed', this.handleBreakpointChange)
    window.removeEventListener('resize', this.handleResize)
  }

  handleResize() {
    // Only update if in full-width mode
    if (this.currentWidthValue === '100%') {
      this.updateBreakpointIndicator('100%')
    }
  }

  handleBreakpointChange(event) {
    const { breakpoint } = event.detail
    this.setWidthFromBreakpoint(breakpoint)
  }

  setWidth(event) {
    const width = event.params.width
    this.currentWidthValue = width

    if (width === '100%') {
      this.contentWrapper.style.maxWidth = ''
      this.contentWrapper.style.margin = ''
      this.contentWrapper.style.transition = ''
    } else {
      this.contentWrapper.style.maxWidth = `${width}px`
      this.contentWrapper.style.margin = '0 auto'
      this.contentWrapper.style.transition = 'max-width 0.2s ease-out'
    }

    this.updateActiveButton(width)
    this.updateBreakpointIndicator(width)

    // Dispatch custom event for other components to listen to
    this.dispatch('widthChanged', { detail: { width } })
  }

  updateActiveButton(activeWidth) {
    // Remove active class from all buttons in this controller
    this.element.querySelectorAll('.toolbar-btn').forEach((btn) => {
      btn.classList.remove('active')
    })

    // Find and activate the matching button
    const activeBtn = this.element.querySelector(`[data-device-preview-width-param="${activeWidth}"]`)
    if (activeBtn) {
      activeBtn.classList.add('active')
    }
  }

  // Allow external components to set width programmatically
  setWidthFromBreakpoint(breakpoint) {
    const widthMap = {
      base: '375',
      sm: '640',
      md: '768',
      lg: '1024',
      xl: '1280',
      '2xl': '100%'
    }

    const width = widthMap[breakpoint] || '100%'
    this.currentWidthValue = width

    if (width === '100%') {
      this.contentWrapper.style.maxWidth = ''
      this.contentWrapper.style.margin = ''
    } else {
      this.contentWrapper.style.maxWidth = `${width}px`
      this.contentWrapper.style.margin = '0 auto'
    }

    this.updateActiveButton(width)
    this.updateBreakpointIndicator(width)
  }

  // Update the breakpoint indicator to show current breakpoint
  updateBreakpointIndicator(width) {
    const pixelWidth = this.getPixelWidth(width)
    const breakpoint = this.getBreakpointForWidth(pixelWidth)
    const info = this.constructor.breakpoints[breakpoint]

    // Update indicator target if present
    if (this.hasIndicatorTarget) {
      this.indicatorTarget.textContent = `${info.icon} ${info.label} (${breakpoint})`
      this.indicatorTarget.dataset.breakpoint = breakpoint
    }

    // Update dimensions target if present
    if (this.hasDimensionsTarget) {
      if (width === '100%') {
        this.dimensionsTarget.textContent = `${window.innerWidth} x ${window.innerHeight}`
      } else {
        this.dimensionsTarget.textContent = `${pixelWidth}px`
      }
    }

    // Dispatch event for other components
    this.dispatch('breakpointChanged', {
      detail: {
        breakpoint,
        width: pixelWidth,
        label: info.label
      }
    })
  }

  // Convert width value to pixels
  getPixelWidth(width) {
    if (width === '100%') {
      return window.innerWidth
    }
    return parseInt(width, 10)
  }

  // Determine which breakpoint a pixel width falls into
  getBreakpointForWidth(pixelWidth) {
    const breakpoints = this.constructor.breakpoints

    for (const [name, config] of Object.entries(breakpoints)) {
      if (pixelWidth >= config.min && pixelWidth <= config.max) {
        return name
      }
    }

    return '2xl' // Default to largest
  }

  // Cycle to the next breakpoint
  cycleBreakpoint() {
    const order = ['base', 'sm', 'md', 'lg', 'xl', '2xl']
    const currentBreakpoint = this.getBreakpointForWidth(this.getPixelWidth(this.currentWidthValue))
    const currentIndex = order.indexOf(currentBreakpoint)
    const nextIndex = (currentIndex + 1) % order.length
    const nextBreakpoint = order[nextIndex]

    this.setWidthFromBreakpoint(nextBreakpoint)
  }

  // Get all breakpoint info for rendering UI
  get breakpointInfo() {
    return this.constructor.breakpoints
  }
}
